import styled, { keyframes } from "styled-components";
import { blueColor, goldColor } from "../../../../styled/Theme";
import { StarFilled } from "@ant-design/icons";
import { testimonialData } from "../testimonials/testimonialData";

const row1 = [...testimonialData.slice(0, 3), ...testimonialData.slice(0, 3)];
const row2 = [...testimonialData.slice(3), ...testimonialData.slice(3)];

export default function Testimonials() {
  return (
    <Wrapper>
      {/* Header */}
      <Header className="container">
        <HeaderInner
          data-aos="fade-up"
          data-aos-duration="650"
          data-aos-delay="80"
        >
          <Eyebrow>
            <EyebrowDot />
            Testimonials
          </Eyebrow>
          <SectionTitle>
            What Our <GoldWord>Users</GoldWord> &amp;{" "}
            <GoldWord>Partners</GoldWord> Say
          </SectionTitle>
          <SectionSubtitle>
            Hear directly from the organisations and individuals using OnePayX
            across Uganda.
          </SectionSubtitle>
        </HeaderInner>

        {/* Star rating summary */}
        <RatingSummary
          data-aos="fade-up"
          data-aos-duration="600"
          data-aos-delay="200"
        >
          <RatingScore>4.9</RatingScore>
          <RatingDetail>
            <Stars>
              {[...Array(5)].map((_, i) => (
                <StarFilled key={i} />
              ))}
            </Stars>
            <RatingLabel>from 200+ verified users</RatingLabel>
          </RatingDetail>
        </RatingSummary>
      </Header>

      {/* Marquee row 1 — scrolls left */}
      <MarqueeOuter $fade>
        <MarqueeTrack $dir="left">
          {row1.map((t, i) => (
            <TestiCard key={i} $color={t.color}>
              <QuoteMark>"</QuoteMark>
              <TestiText>{t.text}</TestiText>
              <TestiFooter>
                <Avatar $color={t.color}>{t.initials}</Avatar>
                <AuthorText>
                  <AuthorName>{t.name}</AuthorName>
                  <AuthorDiv>{t.division}</AuthorDiv>
                </AuthorText>
                <CardStars>
                  {[...Array(5)].map((_, j) => (
                    <StarFilled key={j} />
                  ))}
                </CardStars>
              </TestiFooter>
            </TestiCard>
          ))}
        </MarqueeTrack>
      </MarqueeOuter>

      {/* Marquee row 2 — scrolls right (reverse) */}
      <MarqueeOuter $fade>
        <MarqueeTrack $dir="right">
          {row2.map((t, i) => (
            <TestiCard key={i} $color={t.color}>
              <QuoteMark>"</QuoteMark>
              <TestiText>{t.text}</TestiText>
              <TestiFooter>
                <Avatar $color={t.color}>{t.initials}</Avatar>
                <AuthorText>
                  <AuthorName>{t.name}</AuthorName>
                  <AuthorDiv>{t.division}</AuthorDiv>
                </AuthorText>
                <CardStars>
                  {[...Array(5)].map((_, j) => (
                    <StarFilled key={j} />
                  ))}
                </CardStars>
              </TestiFooter>
            </TestiCard>
          ))}
        </MarqueeTrack>
      </MarqueeOuter>

      {/* Bottom CTA strip */}
      <CTAStrip className="container">
        <CTAInner
          data-aos="fade-up"
          data-aos-duration="600"
          data-aos-delay="100"
        >
          <CTAText>Join thousands of users across Uganda</CTAText>
          <CTAButton href="#">Get Started Free →</CTAButton>
        </CTAInner>
      </CTAStrip>
    </Wrapper>
  );
}

/* ── Animations ── */
const scrollLeft = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
`;

const scrollRight = keyframes`
  from { transform: translateX(-50%); }
  to   { transform: translateX(0); }
`;

/* ── Styles ── */

const Wrapper = styled.section`
  background: #ffffff;
  padding: 80px 0 72px;
  overflow: hidden;
`;

/* Header */
const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 32px;
  padding: 0 20px;
  margin-bottom: 48px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`;

const HeaderInner = styled.div`
  max-width: 560px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: ${blueColor};
  background: ${blueColor}0f;
  border: 1px solid ${blueColor}28;
  border-radius: 20px;
  padding: 4px 14px;
  margin-bottom: 14px;
`;

const EyebrowDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${goldColor};
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 900;
  color: #0a1628;
  letter-spacing: -1.2px;
  line-height: 1.15;
  margin: 0 0 12px;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const GoldWord = styled.span`
  color: ${goldColor};
`;

const SectionSubtitle = styled.p`
  font-size: 15px;
  color: #64748b;
  line-height: 1.65;
  margin: 0;
`;

/* Rating */
const RatingSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  background: #f8fafc;
  border: 1px solid #e8f0f8;
  border-radius: 16px;
  padding: 16px 22px;
  flex-shrink: 0;
`;

const RatingScore = styled.div`
  font-size: 40px;
  font-weight: 900;
  color: #0a1628;
  letter-spacing: -2px;
  line-height: 1;
`;

const RatingDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Stars = styled.div`
  display: flex;
  gap: 2px;
  color: ${goldColor};
  font-size: 14px;
`;

const RatingLabel = styled.div`
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
`;

/* Marquee */
const MarqueeOuter = styled.div<{ $fade?: boolean }>`
  overflow: hidden;
  margin-bottom: 16px;
  position: relative;

  ${(p) =>
    p.$fade &&
    `
    mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);
    -webkit-mask-image: linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%);
  `}
`;

const MarqueeTrack = styled.div<{ $dir: "left" | "right" }>`
  display: flex;
  gap: 16px;
  width: max-content;
  animation: ${(p) => (p.$dir === "left" ? scrollLeft : scrollRight)} 32s linear
    infinite;

  &:hover {
    animation-play-state: paused;
  }
`;

/* Card */
const TestiCard = styled.div<{ $color: string }>`
  width: 340px;
  flex-shrink: 0;
  background: #ffffff;
  border: 1px solid #e8f0f8;
  border-top: 3px solid ${(p) => p.$color};
  border-radius: 18px;
  padding: 24px 22px 20px;
  position: relative;
  transition: all 0.25s ease;
  cursor: default;

  &:hover {
    box-shadow: 0 12px 36px ${(p) => p.$color}18;
    border-color: ${(p) => p.$color}40;
    border-top-color: ${(p) => p.$color};
    transform: translateY(-4px);
  }
`;

const QuoteMark = styled.div`
  font-size: 52px;
  line-height: 0.7;
  color: #e2e8f0;
  font-family: Georgia, serif;
  font-weight: 900;
  margin-bottom: 10px;
  user-select: none;
`;

const TestiText = styled.p`
  font-size: 14px;
  color: #334155;
  line-height: 1.7;
  margin: 0 0 18px;
  flex: 1;
`;

const TestiFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div<{ $color: string }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.3px;
`;

const AuthorText = styled.div`
  flex: 1;
`;

const AuthorName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
`;

const AuthorDiv = styled.div`
  font-size: 11.5px;
  color: #94a3b8;
  margin-top: 1px;
`;

const CardStars = styled.div`
  display: flex;
  gap: 1px;
  color: ${goldColor};
  font-size: 10px;
`;

/* CTA strip */
const CTAStrip = styled.div`
  padding: 0 20px;
  margin-top: 48px;
`;

const CTAInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 24px 32px;
  background: linear-gradient(110deg, #07142e 0%, #0e2458 60%, #091a40 100%);
  border-radius: 20px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 24px;
  }
`;

const CTAText = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.3px;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${goldColor};
  color: #07142e;
  font-size: 14px;
  font-weight: 800;
  border-radius: 10px;
  padding: 12px 24px;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.22s;

  &:hover {
    background: #d4920a;
    transform: translateX(3px);
  }
`;

// import PageHeader from "../PageHeader";
// import TestimonialSlider from "../testimonials/TestimonialSlider";
// import greeting from "../../../../../assets/img/greeting.jpg";

// const Testimonials = () => {
//   return (
//     <>
//       <PageHeader
//         title="What Our Users and Partners Say"
//         description="Hear directly from the organisations and individuals using OnePayX across Uganda."
//         backgroundImage={greeting}
//       />
//       <TestimonialSlider />
//     </>
//   );
// };

// export default Testimonials;
